import { subYears } from 'date-fns';
import type { ObjectShape } from 'yup';
import * as Yup from 'yup';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PHONE_NUMBER_REGEX } from '@pflegenavi/shared/constants';
export enum MangopayConnectFormStep {
  Step1 = 'Step1',
  Step2 = 'Step2',
}

export const useConnectMangopayServiceProviderAccountValidationSchema = (
  step: MangopayConnectFormStep
): ObjectShape => {
  const { t } = useTranslation();
  // @ts-expect-error - we need to add a default return that is empty, but step will always be one of the enum values
  return useMemo(() => {
    switch (step) {
      case MangopayConnectFormStep.Step1:
        return {
          service_provider: Yup.object().shape({
            name: Yup.string()
              .min(
                2,
                t(
                  'service-provider-app.connect-account.form.valid.company-name'
                )
              )
              .max(
                255,
                t(
                  'service-provider-app.connect-account.form.valid.company-name'
                )
              )
              .matches(
                /^(?!.*[(),.:/]).*/,
                t('mangopay.recipient.errors.forbidden-chars', {
                  chars: '( ) , . : /',
                })
              )
              .required(
                t(
                  'service-provider-app.connect-account.form.required.company-name'
                )
              ),

            company_number: Yup.string().when(
              'legal_person_type',
              ([legal_person_type]) => {
                const baseValidation = Yup.string()
                  .min(
                    1,
                    t(
                      'service-provider-app.connect-account.form.valid.company-number.min'
                    )
                  )
                  .matches(
                    /^\S*$/,
                    t(
                      'service-provider-app.connect-account.form.valid.company-number.no-spaces'
                    )
                  );

                if (legal_person_type === 'SOLETRADER') {
                  return baseValidation;
                } else {
                  return baseValidation.required(
                    t(
                      'service-provider-app.connect-account.form.required.company-number'
                    )
                  );
                }
              }
            ),
            legal_person_type: Yup.string()
              .oneOf(
                ['BUSINESS', 'ORGANIZATION', 'SOLETRADER'],
                t(
                  'service-provider-app.connect-account.form.valid.legal-person-type'
                )
              )
              .required(
                t(
                  'service-provider-app.connect-account.form.required.legal-person-type'
                )
              ),
            email: Yup.string()
              .email(t('service-provider-app.connect-account.form.valid.email'))
              .required(
                t('service-provider-app.connect-account.form.required.email')
              ),
            phone: Yup.string()
              .matches(PHONE_NUMBER_REGEX, {
                message: t(
                  'service-provider-app.connect-account.form.valid.phone'
                ),
              })
              .required(
                t('service-provider-app.connect-account.form.required.phone')
              ),
            headquarters_address: Yup.object().shape({
              address: Yup.string()
                .min(
                  4,
                  t('service-provider-app.connect-account.form.valid.address')
                )
                .max(
                  255,
                  t('service-provider-app.connect-account.form.valid.address')
                )
                .matches(
                  /^(?!.*[()/]).*/,
                  t('mangopay.recipient.errors.forbidden-chars', {
                    chars: '( ) /',
                  })
                )
                .required(
                  t(
                    'service-provider-app.connect-account.form.required.address'
                  )
                ),
              zip_code: Yup.string()
                .min(
                  3,
                  t('service-provider-app.connect-account.form.valid.zip-code')
                )
                .max(
                  9,
                  t('service-provider-app.connect-account.form.valid.zip-code')
                )
                .matches(
                  /^(?!.*[()&,.:_'/]).*/,
                  t('mangopay.recipient.errors.forbidden-chars', {
                    chars: "( ) & , . : _ ' /",
                  })
                )
                .required(
                  t(
                    'service-provider-app.connect-account.form.required.zip-code'
                  )
                ),
              city: Yup.string()
                .min(
                  2,
                  t('service-provider-app.connect-account.form.valid.city')
                )
                .max(
                  255,
                  t('service-provider-app.connect-account.form.valid.city')
                )
                .matches(
                  /^(?!.*[&,.:_']).*/,
                  t('mangopay.recipient.errors.forbidden-chars', {
                    chars: "& , . : _ '",
                  })
                )
                .required(
                  t('service-provider-app.connect-account.form.required.city')
                ),
              country_id: Yup.string().required(
                t(
                  'service-provider-app.connect-account.form.required.country-id'
                )
              ),
            }),
          }),
        };

      case MangopayConnectFormStep.Step2:
        return {
          legal_representative: Yup.object().shape({
            first_name: Yup.string()
              .min(
                2,
                t('service-provider-app.connect-account.form.valid.first-name')
              )
              .max(
                255,
                t('service-provider-app.connect-account.form.valid.first-name')
              )
              .matches(
                /^(?!.*[()&,.:_/]).*/,
                t('mangopay.recipient.errors.forbidden-chars', {
                  chars: '( ) & , . : _ /',
                })
              )
              .required(
                t(
                  'service-provider-app.connect-account.form.required.first-name'
                )
              ),
            last_name: Yup.string()
              .min(
                2,
                t('service-provider-app.connect-account.form.valid.last-name')
              )
              .max(
                255,
                t('service-provider-app.connect-account.form.valid.last-name')
              )
              .matches(
                /^(?!.*[()&,.:_/]).*/,
                t('mangopay.recipient.errors.forbidden-chars', {
                  chars: '( ) & , . : _ /',
                })
              )
              .required(
                t(
                  'service-provider-app.connect-account.form.required.last-name'
                )
              ),
            birth_date: Yup.date()
              .required(
                t(
                  'service-provider-app.connect-account.form.required.birthdate'
                )
              )
              .typeError(t('field.valid-date'))
              .min(new Date(1900, 0, 1), t('field.valid-date'))
              .max(subYears(new Date(), 18), t('field.valid-is-adult')),
            nationality_id: Yup.string().required(
              t(
                'service-provider-app.connect-account.form.required.nationality-id'
              )
            ),
            country_of_residence_id: Yup.string().required(
              t(
                'service-provider-app.connect-account.form.required.country-of-residence-id'
              )
            ),
          }),
        };

      default:
        return {};
    }
  }, [step, t]);
};
